/**
 * Extract a user-friendly error message from an API error response.
 */
export function extractError(error: unknown, fallback = 'An unexpected error occurred'): string {
  if (!error) return fallback;

  if (typeof error === 'string') return error;

  const err = error as any;

  // Axios error shape: server responded with an error body.
  if (err.response?.data?.message) {
    const msg = err.response.data.message;
    return Array.isArray(msg) ? msg.join('. ') : String(msg);
  }

  // Axios error with no response: request never reached the server, was
  // blocked (CORS, offline, in-app browser restrictions), or timed out.
  // Surfacing the raw axios message ("Network Error") isn't useful to users.
  if (err.isAxiosError && !err.response) {
    return err.code === 'ECONNABORTED'
      ? 'The request timed out. Please check your connection and try again.'
      : 'Unable to reach the server. Please check your internet connection and try again.';
  }

  if (err.message) return String(err.message);

  return fallback;
}

/**
 * True when an axios error never received a server response (network
 * failure, CORS block, or timeout) as opposed to the server responding
 * with an error status.
 */
export function isNetworkError(error: unknown): boolean {
  const err = error as any;
  return Boolean(err?.isAxiosError && !err.response);
}
